/**
 * Operator-Katalog für Testdaten-Bedingungen und Zeilen-Filter (NEX-1049).
 *
 * <p>Datenmodell: {@link TdbOverride}.{@code op} traegt IMMER den Backend-Code
 * (z. B. {@code 'EQUAL'}), nie das lokalisierte Label — sonst haengt jede
 * Backend-Interaktion an der eingestellten Sprache.
 *
 * <p>Die {@code labelKey}-Angabe ist der i18n-Schluessel; das Template loest ihn
 * ueber die {@code translate}-Pipe auf.
 *
 * <p>{@code aliases} spiegelt die Operator-Mengen aus
 * `ConditionOperators.java:21-29` — {@code fromStepCondition} akzeptiert dadurch
 * auch die Classic-Schreibweisen ({@code EQUALS}, {@code GREATER_THAN},
 * {@code GRÖSSER}, {@code NICHTLEER}).
 */
export interface TcdlOperator {
  readonly code: string;
  /** i18n-Schluessel fuer das UI-Dropdown-Label. */
  readonly labelKey: string;
  /**
   * Deutsches TCDL-Schluesselwort fuer die Code-Vorschau. Bewusst NICHT ueber i18n,
   * weil das Vorschaufeld die tatsaechliche TCDL-Syntax zeigt (die die Engine so
   * parst). Nur das UI-Dropdown-Label darf lokalisieren.
   */
  readonly tcdlWord: string;
  readonly needsValue: boolean;
  readonly aliases?: readonly string[];
}

export const TCDL_OPS: readonly TcdlOperator[] = [
  {
    code: 'EQUAL',
    labelKey: 'tdb.op.EQUAL',
    tcdlWord: 'GLEICH',
    needsValue: true,
    aliases: ['GLEICH', 'EQUALS'],
  },
  {
    code: 'NOT_EQUAL',
    labelKey: 'tdb.op.NOT_EQUAL',
    tcdlWord: 'UNGLEICH',
    needsValue: true,
    aliases: ['UNGLEICH', 'NOT_EQUALS'],
  },
  {
    code: 'GREATER',
    labelKey: 'tdb.op.GREATER',
    tcdlWord: 'GROESSER',
    needsValue: true,
    aliases: ['GROESSER', 'GRÖSSER', 'GREATER_THAN'],
  },
  {
    code: 'GREATER_OR_EQUAL',
    labelKey: 'tdb.op.GREATER_OR_EQUAL',
    tcdlWord: 'GROESSERGLEICH',
    needsValue: true,
    aliases: ['GROESSERGLEICH', 'GRÖSSERGLEICH'],
  },
  {
    code: 'LESS',
    labelKey: 'tdb.op.LESS',
    tcdlWord: 'KLEINER',
    needsValue: true,
    aliases: ['KLEINER', 'LESS_THAN'],
  },
  {
    code: 'LESS_OR_EQUAL',
    labelKey: 'tdb.op.LESS_OR_EQUAL',
    tcdlWord: 'KLEINERGLEICH',
    needsValue: true,
    aliases: ['KLEINERGLEICH'],
  },
  { code: 'EMPTY', labelKey: 'tdb.op.EMPTY', tcdlWord: 'LEER', needsValue: false, aliases: ['LEER'] },
  {
    code: 'NOT_EMPTY',
    labelKey: 'tdb.op.NOT_EMPTY',
    tcdlWord: 'NICHT LEER',
    needsValue: false,
    aliases: ['NICHT LEER', 'NICHTLEER'],
  },
];

/** Subjekt der Bedingung: Wertvergleich oder Vorhandensein eines Objekts / Fensters (NEX-1084). */
export type ConditionSubject = 'WERT' | 'OBJEKT' | 'FENSTER';

/**
 * Operatoren fuer OBJEKT und FENSTER — Classic `AUSFÜHREN WENN OBJEKT [NICHT] VORHANDEN`.
 * Kein Vergleichswert, keine Aliase: Der Code steht nur im UI-Zustand, das BE kennt die
 * Art ({@code OBJECT_EXISTS} …), nicht den Operator.
 */
export const PRESENCE_OPS: readonly TcdlOperator[] = [
  { code: 'EXISTS', labelKey: 'tdb.presence.EXISTS', tcdlWord: 'VORHANDEN', needsValue: false },
  { code: 'NOT_EXISTS', labelKey: 'tdb.presence.NOT_EXISTS', tcdlWord: 'NICHT VORHANDEN', needsValue: false },
];

export function operatorsFor(subj: ConditionSubject | '' | undefined): readonly TcdlOperator[] {
  if (subj === 'WERT') return TCDL_OPS;
  if (subj === 'OBJEKT' || subj === 'FENSTER') return PRESENCE_OPS;
  return [];
}

/**
 * Sucht den Operator passend zum Subjekt. Ein Wert-Operator unter OBJEKT (oder umgekehrt)
 * gilt als nicht gesetzt — so bleibt er nach einem Wechsel der Art im Entwurf, wird aber
 * weder angezeigt noch gespeichert.
 */
export function findOperatorFor(
  subj: ConditionSubject | '' | undefined,
  candidate: string | null | undefined,
): TcdlOperator | undefined {
  if (subj === 'WERT') return findOperator(candidate);
  const upper = candidate?.trim().toUpperCase();
  return upper ? operatorsFor(subj).find((o) => o.code === upper) : undefined;
}

/**
 * UI-Zustand einer einzelnen Testdaten-Ueberlagerung.
 *
 * <p>{@code op} traegt den Operator-Code aus {@link TCDL_OPS} (z. B. {@code 'EQUAL'}),
 * nie das lokalisierte Label — sonst ist jede Backend-Interaktion sprachabhaengig.
 */
export interface TdbOverride {
  filter?: string;
  subj?: ConditionSubject | '';
  ref?: string;
  op?: string;
  val?: string;
}

/**
 * Sucht einen Operator nach seinem Code oder einem seiner Backend-Aliase.
 * Klein-/Grossbuchstaben-tolerant.
 */
export function findOperator(candidate: string | null | undefined): TcdlOperator | undefined {
  if (!candidate) return undefined;
  const upper = candidate.trim().toUpperCase();
  if (!upper) return undefined;
  return TCDL_OPS.find((o) => o.code === upper || (o.aliases?.some((a) => a.toUpperCase() === upper) ?? false));
}

/**
 * TCDL-Fragment fuer eine gesetzte Bedingung — ohne Aufloesung des Labels, damit
 * der Aufrufer den i18n-Kontext bestimmt. Rueckgabe ist der Operator-Code plus
 * die Operanden, oder {@code null} wenn keine ausfuehrbare Bedingung vorliegt.
 */
export interface BedingungFragment {
  /** Fehlt bei Wertvergleichen (WERT), damit bestehende Fragmente gleich bleiben. */
  readonly subject?: ConditionSubject;
  readonly operatorCode: string;
  readonly operand: string;
  readonly value?: string;
}

/**
 * Schluesselwoerter der TCDL-Code-Vorschau. Bewusst Konstanten statt i18n: Das
 * Vorschaufeld zeigt die Syntax, die die Engine parst
 * (`engine-core/src/tcdl/parser.ts`). Ueber i18n gefuehrt wurde daraus auf
 * Englisch `RUN "…" WHERE VALUE "…"` — kein gueltiges TCDL (sgraetz-Review
 * NEX-1049). Lokalisiert wird nur das Dropdown-Label ({@link TcdlOperator.labelKey}).
 */
export const TCDL_KEYWORDS = {
  execute: 'AUSFÜHREN',
  when: 'WENN',
  valueSubject: 'WERT',
} as const;

/**
 * Baut die Code-Vorschau eines Modulaufrufs:
 * {@code AUSFÜHREN "<Modul>" "<TDB|Filter>" WENN WERT "<Ref>" <OP> "<Wert>"}, bei OBJEKT und
 * FENSTER {@code … WENN OBJEKT "<Name>" [NICHT] VORHANDEN}. Ohne Datenblatt (leeres
 * {@code idSegment}) faellt das zweite Segment weg.
 */
export function formatTcdlPreview(moduleTitle: string, idSegment: string, fragment: BedingungFragment | null): string {
  const base = idSegment
    ? `${TCDL_KEYWORDS.execute} "${moduleTitle}" "${idSegment}"`
    : `${TCDL_KEYWORDS.execute} "${moduleTitle}"`;
  if (!fragment) return base;
  const subject = fragment.subject ?? TCDL_KEYWORDS.valueSubject;
  const tcdlWord = findOperatorFor(subject, fragment.operatorCode)?.tcdlWord ?? fragment.operatorCode;
  const condition =
    fragment.value === undefined
      ? `${subject} "${fragment.operand}" ${tcdlWord}`
      : `${subject} "${fragment.operand}" ${tcdlWord} "${fragment.value}"`;
  return `${base} ${TCDL_KEYWORDS.when} ${condition}`;
}

export function tdbIdSegment(tdbKey: string, filter: string | null | undefined): string {
  const trimmed = (filter ?? '').trim();
  return trimmed ? `${tdbKey}|${trimmed}` : tdbKey;
}

export function bedingungFragment(ov: TdbOverride | null | undefined): BedingungFragment | null {
  if (!ov || !ov.subj || !ov.op) return null;
  const op = findOperatorFor(ov.subj, ov.op);
  if (!op) return null;
  const ref = (ov.ref ?? '').trim();
  if (!ref) return null;
  if (ov.subj !== 'WERT') return { subject: ov.subj, operatorCode: op.code, operand: ref };
  if (op.needsValue) {
    const val = (ov.val ?? '').trim();
    if (!val) return null;
    return { operatorCode: op.code, operand: ref, value: val };
  }
  return { operatorCode: op.code, operand: ref };
}
